import type { Type } from '@angular/core';
import type { Routes } from '@angular/router';

/**
 * Satu item menu halaman "Perbarui Dokumen" = satu route anak `/vendor/profile/<slug>`.
 */
export interface ProfileSection {
  /**
   * Segmen URL. Underscore, BUKAN kebab-case — mengikuti konvensi path area vendor yang sudah ada
   * (`expired_doc_status`, `comment_history`) yang berasal dari `navigable.path` menu BE.
   */
  readonly slug: string;
  /**
   * Key i18n LITERAL. Nilainya WAJIB ada di `public/i18n/en.json` DAN `id.json` — key yang hanya
   * ada di satu bahasa dirender sebagai key mentah, tanpa error.
   *
   * Ini bukan "key dinamis" yang dilarang: key-nya tidak disusun dari nilai (`'panel.' + slug`),
   * melainkan string literal yang seluruhnya terlihat di berkas ini. Pola yang sama dipakai
   * `TABS` di `vendor-profile.ts` dan `PANELS` di `panel-registry.ts`.
   */
  readonly labelKey: string;
  /** Key i18n untuk penjelasan panjang; dipakai sebagai tooltip item menu. Opsional. */
  readonly descriptionKey?: string;
  /**
   * Komponen isi kanan. `undefined` → memakai `SectionPlaceholderComponent`.
   *
   * Saat sebuah section akhirnya dikerjakan, isi field ini — tidak ada berkas lain yang perlu
   * disentuh, route-nya ikut berubah dengan sendirinya (lihat `vendor.routes.ts`).
   */
  readonly load?: () => Promise<Type<unknown>>;
}

export interface ProfileSectionGroup {
  readonly titleKey: string;
  readonly items: readonly ProfileSection[];
}

/**
 * Menu halaman "Perbarui Dokumen" — **DIPAKAI DUA KALI**: membangun `children` route
 * (`vendor.routes.ts`) dan merender sidebar (`profile-edit.html`).
 *
 * Jangan menduplikasi daftarnya di template. Dua daftar yang lama-lama berbeda muncul sebagai
 * menu yang menunjuk 404 — bukan sebagai error build.
 *
 * Urutan array = urutan tampil. Peta lengkap label ↔ slug ↔ `PanelKey`:
 * `plans/vendors/profile/PLAN-PROFILE-EDIT-SHELL.md` §4.
 *
 * Label ke-14 panel MEMAKAI ULANG `vendorProfile.panel.*` yang sudah dipakai dashboard
 * kelengkapan (`profile-tab`), supaya menu dan dashboard tidak pernah menyebut dokumen yang sama
 * dengan dua nama berbeda. Hanya `companyProfile` yang key-nya baru.
 */
export const PROFILE_SECTION_GROUPS: readonly ProfileSectionGroup[] = [
  {
    titleKey: 'vendorProfile.edit.group.administration',
    items: [
      {
        slug: 'organizations',
        labelKey: 'vendorProfile.panel.generalData',
        load: () =>
          import('../pages/profile-edit/sections/organizations/organizations').then(
            (c) => c.OrganizationsSectionComponent,
          ),
      },
      {
        // ⚠️ Slug & label DUA section pertama bersilangan: `organizations` berlabel "Umum"
        // (`panel.generalData`), sedangkan `general_data` berlabel "Profil Perusahaan"
        // (`panel.companyProfile`). Jangan "diperbaiki" — slugnya mengikuti `navigable.path` BE.
        slug: 'general_data',
        labelKey: 'vendorProfile.panel.companyProfile',
        load: () =>
          import('../pages/profile-edit/sections/general-data/general-data').then(
            (c) => c.GeneralDataSectionComponent,
          ),
      },
      {
        // Slug `legal_bases`, label "Akta", endpoint `profile_deeds`, `PanelKey` `LEGAL_BASIS` —
        // empat nama untuk satu panel yang sama. Jangan menyeragamkan salah satunya.
        slug: 'legal_bases',
        labelKey: 'vendorProfile.panel.legalBasis',
        load: () =>
          import('../pages/profile-edit/sections/legal-bases/legal-bases').then(
            (c) => c.LegalBasesSectionComponent,
          ),
      },
      {
        slug: 'shareholders',
        labelKey: 'vendorProfile.panel.shareholder',
        load: () =>
          import('../pages/profile-edit/sections/shareholders/shareholders').then(
            (c) => c.ShareholdersSectionComponent,
          ),
      },
      {
        slug: 'boards',
        labelKey: 'vendorProfile.panel.board',
        load: () =>
          import('../pages/profile-edit/sections/boards/boards').then(
            (c) => c.BoardsSectionComponent,
          ),
      },
      {
        slug: 'business_permits',
        labelKey: 'vendorProfile.panel.businessPermit',
        load: () =>
          import('../pages/profile-edit/sections/business-permits/business-permits').then(
            (c) => c.BusinessPermitsSectionComponent,
          ),
      },
      {
        slug: 'person_in_charges',
        labelKey: 'vendorProfile.panel.personInCharge',
        load: () =>
          import('../pages/profile-edit/sections/person-in-charges/person-in-charges').then(
            (c) => c.PersonInChargesSectionComponent,
          ),
      },
    ],
  },
  {
    titleKey: 'vendorProfile.edit.group.competency',
    items: [
      {
        slug: 'tools',
        labelKey: 'vendorProfile.panel.tools',
        load: () =>
          import('../pages/profile-edit/sections/tools/tools').then((c) => c.ToolsSectionComponent),
      },
      {
        slug: 'experts',
        labelKey: 'vendorProfile.panel.experts',
        load: () =>
          import('../pages/profile-edit/sections/experts/experts').then(
            (c) => c.ExpertsSectionComponent,
          ),
      },
      {
        slug: 'certifications',
        labelKey: 'vendorProfile.panel.certification',
        load: () =>
          import('../pages/profile-edit/sections/certifications/certifications').then(
            (c) => c.CertificationsSectionComponent,
          ),
      },
      {
        slug: 'competencies',
        labelKey: 'vendorProfile.panel.competency',
        load: () =>
          import('../pages/profile-edit/sections/competencies/competencies').then(
            (c) => c.CompetenciesSectionComponent,
          ),
      },
      {
        // Label menu sengaja PENDEK. Kalimat panjangnya ("Data pengalaman kerja berupa pekerjaan
        // yang telah selesai dikerjakan") jadi tooltip: 60 karakter tidak terbaca di menu selebar
        // 18rem, dan `mat-list-item` bertinggi TETAP — teks yang membungkus akan meluber dari
        // kotaknya (jebakan yang sama sudah dicatat di `profile-tab.html`).
        slug: 'portfolios',
        labelKey: 'vendorProfile.panel.experience',
        descriptionKey: 'vendorProfile.edit.section.portfoliosDescription',
        load: () =>
          import('../pages/profile-edit/sections/portfolios/portfolios').then(
            (c) => c.PortfoliosSectionComponent,
          ),
      },
    ],
  },
  {
    titleKey: 'vendorProfile.edit.group.finance',
    items: [
      {
        slug: 'bank_accounts',
        labelKey: 'vendorProfile.panel.bankAccount',
        load: () =>
          import('../pages/profile-edit/sections/bank-accounts/bank-accounts').then(
            (c) => c.BankAccountsSectionComponent,
          ),
      },
      {
        slug: 'financial_statements',
        labelKey: 'vendorProfile.panel.financialStatement',
        load: () =>
          import('../pages/profile-edit/sections/financial-statements/financial-statements').then(
            (c) => c.FinancialStatementsSectionComponent,
          ),
      },
      {
        // Slug `tax_documents`, endpoint `profile_tax_docs` — dua nama untuk panel yang sama.
        // Jangan menyeragamkan salah satunya.
        slug: 'tax_documents',
        labelKey: 'vendorProfile.panel.taxDocument',
        load: () =>
          import('../pages/profile-edit/sections/tax-documents/tax-documents').then(
            (c) => c.TaxDocumentsSectionComponent,
          ),
      },
    ],
  },
];

/** Rata — untuk membangun `children` route. Turunan dari grup, jangan ditulis ulang. */
export const PROFILE_SECTIONS: readonly ProfileSection[] = PROFILE_SECTION_GROUPS.flatMap(
  (group) => group.items,
);

/**
 * `children` route shell — **SATU perakit untuk DUA situs**: `/vendor/profile/<slug>` (mode `edit`)
 * dan `/admin/vendors/:id/<slug>` (mode `review`).
 *
 * Ditaruh di sini, bukan dirakit inline di masing-masing berkas route: "daftar section" dan
 * "perakit route dari daftar" harus tinggal serumah. Dua salinan yang lama-lama berbeda muncul
 * sebagai menu yang menunjuk 404 — bukan sebagai error build.
 *
 * `data.labelKey` diteruskan supaya isi kanan tahu judul section-nya tanpa mencocokkan URL
 * sendiri. `title` memakai key i18n (`I18nTitleStrategy`), bukan string mentah.
 *
 * ⚠️ **Tanpa `data.profileEditMode`.** Mode & vendor id dibaca dari route SHELL
 * (`provideProfileEditContext()`), jadi menaruhnya di sini berarti 15 salinan yang bisa berbeda
 * satu sama lain — dan section yang modenya salah tidak menghasilkan error apa pun.
 */
export function buildProfileEditChildren(): Routes {
  return PROFILE_SECTIONS.map((section) => ({
    path: section.slug,
    loadComponent:
      section.load ??
      (() =>
        import('../pages/profile-edit/sections/section-placeholder').then(
          (c) => c.SectionPlaceholderComponent,
        )),
    title: section.labelKey,
    data: { labelKey: section.labelKey, descriptionKey: section.descriptionKey },
  }));
}
